import { uiText } from "@/lib/i18n/uiText";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bot, Copy, Check, KeyRound, LogIn, Coins, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/lib/useSEO";
import { breadcrumbJsonLd, faqPageJsonLd, organizationJsonLd, webPageJsonLd, webApiJsonLd } from "@/lib/seoSchema";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { AiAccessSubscription } from "@/components/premium/AiAccessSubscription";
import { CreditPacks } from "@/components/premium/CreditPacks";

const SITE_URL = "https://statlign.com";
/** Sign-in (OAuth) endpoint for AI assistants such as ChatGPT, Claude and Cursor. */
export const MCP_ENDPOINT = `${SITE_URL}/mcp`;
/** Secret-key endpoint for bots, scripts and server-to-server agents. */
export const MCP_KEY_ENDPOINT = `${SITE_URL}/api/public/mcp`;

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "AI Access", path: "/ai-access" },
];

const TOOLS = [
  ["list_upcoming_predictions", "Upcoming football picks: teams, kickoff, odds, confidence."],
  ["list_high_odds_predictions", "Upcoming high-value odds picks (sign-in endpoint)."],
  ["list_safe_bets", "Lower-odds, higher-confidence picks (sign-in endpoint)."],
  ["list_us_sports_predictions", "NFL, NBA, MLB and NHL picks (sign-in endpoint)."],
  ["search_predictions", "Filter picks by team, league, date and odds (sign-in endpoint)."],
  ["get_prediction_details", "Full detail for one prediction."],
  ["get_track_record", "Graded results and win rate."],
  ["reveal_prediction", "Unlock a pick (2 credits) or its strategy insights (1 credit)."],
  ["get_my_credits", "Your credit balance."],
] as const;

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(uiText("Copied to clipboard"));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(uiText("Could not copy"));
    }
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-lg border bg-muted/50 p-4 pr-12 text-xs leading-relaxed">
        <code>{code}</code>
      </pre>
      <Button size="sm" variant="ghost" className="absolute top-2 right-2 h-7 gap-1" onClick={copy} aria-label={uiText("Copy code")}>
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

export default function AiAccess() {
  useSEO({
    title: "AI Access — Connect AI Agents to Statlign (MCP)",
    description:
      "Connect ChatGPT, Claude, Cursor or your own bot to Statlign's sports intelligence over MCP at statlign.com/mcp. Sign in or use a secret key; reveals use account credits.",
    canonicalPath: "/ai-access",
    keywords: "Statlign MCP, AI agent access, LLM sports data API, model context protocol",
    jsonLd: [
      organizationJsonLd(),
      webPageJsonLd({
        name: "AI Access — Statlign MCP for AI agents",
        description: "Connect AI assistants and bots to Statlign's MCP server at statlign.com/mcp.",
        path: "/ai-access",
      }),
      webApiJsonLd({
        name: "Statlign MCP server",
        description: "Paid MCP access to Statlign sports predictions. Sign-in (OAuth) or secret-key authentication; reveals spend account credits.",
        path: "/ai-access",
        documentation: `${SITE_URL}/ai-access`,
      }),
      breadcrumbJsonLd(CRUMBS),
      faqPageJsonLd([
        {
          question: "Is Statlign's MCP free?",
          answer:
            "No. OAuth assistants and secret-key bots both require an active $25/month AI Access plan. Revealing a pick costs 2 wallet credits and strategy insights cost 1 credit. Purchased packs fund the same wallet; they do not replace the subscription.",
        },
        {
          question: "What is the MCP address?",
          answer:
            "AI assistants connect to https://statlign.com/mcp and sign in with their Statlign account. Bots with a secret key call https://statlign.com/api/public/mcp.",
        },
      ]),
    ],
  });

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <Breadcrumbs items={CRUMBS} />

      <header className="mt-4 mb-8">
        <div className="mb-3 flex items-center gap-2">
          <Bot className="h-6 w-6 text-primary" />
          <Badge variant="secondary">{uiText("Model Context Protocol")}</Badge>
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{uiText("Connect AI agents to Statlign")}</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          {uiText("Give ChatGPT, Claude, Cursor or your own bot direct access to Statlign's picks, odds and results. MCP access is a paid service: every call runs on a Statlign account and reveals spend that account's credits.")}
        </p>
      </header>

      {/* Pricing */}
      <Card className="mb-6 border-primary/40">
        <CardHeader>
          <CardTitle as="h2" className="flex items-center gap-2 text-lg">
            <Coins className="h-5 w-5 text-primary" /> {uiText("What it costs")}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-lg border p-3">
            <p className="font-semibold">{uiText("2 credits")}</p>
            <p className="text-muted-foreground">{uiText("to reveal one upcoming pick")}</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="font-semibold">{uiText("1 credit")}</p>
            <p className="text-muted-foreground">{uiText("to unlock its strategy insights")}</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="font-semibold">{uiText("$25 / month")}</p>
            <p className="text-muted-foreground">{uiText("AI Access plan — required for secret keys")}</p>
          </div>
          <p className="text-muted-foreground sm:col-span-3">
            {uiText("Played-match results cost no reveal credits. API calls require AI Access; upcoming lists hide picks, odds and confidence until unlocked. Top up credits at")}{" "}
            <a className="underline" href="/wallet">statlign.com/wallet</a>.
          </p>
        </CardContent>
      </Card>

      <CreditPacks />

      {/* Option 1: sign-in */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <LogIn className="h-5 w-5 text-primary" /> {uiText("Option 1 — AI assistants (sign in)")}
          </CardTitle>
          <CardDescription>
            {uiText("For Claude, Cursor and any MCP client that supports sign-in. Add this server address; the assistant opens a Statlign sign-in and an “Allow access” screen. Active AI Access subscription required.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CodeBlock code={MCP_ENDPOINT} />
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{uiText("ChatGPT: Settings → Connectors → Add custom connector → paste the address.")}</li>
            <li>{uiText("Claude: Settings → Connectors → Add custom connector → paste the address.")}</li>
            <li>{uiText("Cursor / other clients: add it as a remote (Streamable HTTP) MCP server.")}</li>
          </ol>
          <CodeBlock
            code={`{
  "mcpServers": {
    "statlign": { "url": "${MCP_ENDPOINT}" }
  }
}`}
          />
        </CardContent>
      </Card>

      {/* Option 2: secret key */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="h-5 w-5 text-primary" /> {uiText("Option 2 — Bots & developers (secret key)")}
          </CardTitle>
          <CardDescription>
            {uiText("For scripts, servers and agents that can't open a sign-in screen. Subscribe to AI Access below, generate your key, and send it as a Bearer token. Calls act as your account and spend your credits.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CodeBlock code={MCP_KEY_ENDPOINT} />
          <CodeBlock
            code={`curl -s -X POST "${MCP_KEY_ENDPOINT}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk_statlign_YOUR_KEY" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/call","params":{"name":"list_upcoming_predictions","arguments":{"limit":5}}}'`}
          />
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            {uiText("Your key is shown once — store it safely. Rotating creates a new key and disables the old one. Each key is limited to 60 requests per minute; if your plan lapses, calls are refused until you renew.")}
          </p>
        </CardContent>
      </Card>

      <div className="mb-6">
        <AiAccessSubscription />
      </div>

      {/* Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{uiText("Available tools")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y">
            {TOOLS.map(([name, desc]) => (
              <li key={name} className="flex flex-col gap-0.5 py-2.5">
                <code className="text-sm font-medium">{name}</code>
                <span className="text-sm text-muted-foreground">{uiText(desc)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">{uiText("Betting involves risk. 18+ only. Please gamble responsibly.")}</p>
        </CardContent>
      </Card>
    </div>
  );
}
